import * as React from 'react';
import {Alert, Checkbox, Flex, FormDropdown, Header, Loader, Text} from "@fluentui/react-northstar";
import './usersettings.scss';
import update from "immutability-helper";
import {ITopic, IUserSelectedTopic} from "../../Interfaces/Topic";
import {ILanguage} from "../../Interfaces/Language";
import {IUserSettings} from "../../Interfaces/UserSettings";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import LanguageApi from "../../apis/languageApi";
import UserSettingsApi from "../../apis/userSettingsApi";

interface IUserSettingsProps {

}

interface IUserSettingsState {
    loaded: boolean,
    availableLanguages: Array<ILanguage>,
    selectedLanguage: ILanguage,
    selectedTopics: Array<boolean>,
    userId: string,
    locale: string,
    upn: string;
    name: string;
}

class UserSettings extends React.PureComponent<IUserSettingsProps, IUserSettingsState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    constructor(props: IUserSettingsProps) {
        super(props);

        this.state = {
            loaded: false,
            availableLanguages: new Array<ILanguage>(),
            selectedLanguage: {} as any,
            selectedTopics: new Array<boolean>(),
            userId: "",
            locale: "",
            upn: "",
            name: ""
        }
    }

    componentDidUpdate(prevProps: Readonly<IUserSettingsProps>, prevState: Readonly<IUserSettingsState>) {
        // Fetch user settings after everything initialized
        if (!prevState.loaded && !this.state.loaded && this.state.availableLanguages.length > 0 && this.context.topics.list.length > 0)
            this.fetchUserSettings();

        // Update user settings if default language or topics has changed
        if (prevState.loaded && this.state.loaded && (prevState.selectedLanguage !== this.state.selectedLanguage ||
            prevState.selectedTopics !== this.state.selectedTopics))
            this.updateUserSettings();
    }

    componentDidMount() {
        // Get user id
        this.setState({
            userId: this.context.teams.userId as string,
            locale: this.context.teams.locale.slice(0, 2).toUpperCase(),
        });

        // Fetch languages
        LanguageApi.getLanguages().then(availableLanguages => {
            const languages = Array.from(availableLanguages.values());
            // Default language is english
            const english = languages.filter(l => l.code === "EN")[0];
            this.setState({
                availableLanguages: languages,
                selectedLanguage: english
            })
        });

        const selectedTopics = this.context.topics.list.map(t => t.activateForAllUsers);
        this.setState({selectedTopics})
    }

    /**
     * Fetch user settings from database
     */
    private fetchUserSettings = () => {
        UserSettingsApi.getUserSettings(this.state.userId).then((data: IUserSettings) => {
            if (data) {
                // Get user language
                const language = this.state.availableLanguages.filter(l => l.id === data.preferedLanguage)[0];
                // Retrieve interested topics ids
                data.selectedTopics.filter(t => t.topicId).map(t => t.topicId);
                // Get user selected topics
                const selectedTopics = this.context.topics.list.map(t => this.isSelectedTopics(t, data.selectedTopics));
                this.setState({
                    selectedLanguage: language,
                    selectedTopics,
                    loaded: true,
                    userId: data.aadId,
                    upn: data.upn,
                    name: data.displayName
                });
            } else {
                this.updateUserSettings();
            }
        });
    }

    private isSelectedTopics = (topic: ITopic, interestingTopics: IUserSelectedTopic[]): boolean => {
        let isSelected: boolean;
        const possibleTopics = interestingTopics.filter(t => t.topicId === topic.id);
        if (possibleTopics.length > 1)
            isSelected = false;
        if (possibleTopics.length == 0)
            isSelected = topic.activateForAllUsers || topic.cannotDeactivate;
        else
            isSelected = topic.cannotDeactivate || possibleTopics[0].isSelected;
        return isSelected;
    };

    /**
     * Get user settings from state
     */
    private getUserSettings = () => {
        // Selected topics ids
        const selectedTopicIds: Array<IUserSelectedTopic> = this.context.topics.list
            .map((t, i) => ({
                topicId: t.id ?? "",
                isSelected: this.state.selectedTopics[i] ?? false
            }));
        // User settings
        return {
            aadId: this.state.userId,
            preferedLanguage: this.state.selectedLanguage && this.state.selectedLanguage.id,
            selectedTopics: selectedTopicIds,
            upn: this.state.upn,
            displayName: this.state.name
        } as IUserSettings;
    }

    /**
     * Update user settings and create them if not existing
     */
    private updateUserSettings = () => {
        // Call API to save user settings
        UserSettingsApi.updateUserSettings(this.getUserSettings()).then(() => {
            if (!this.state.loaded) this.setState({loaded: true})
        });
    }

    render() {
        if (!this.state.loaded) return (
            <Flex fill className={"p-20px"} vAlign={"center"} hAlign={"center"}>
                <Loader/>
            </Flex>
        );

        return (
            <Flex fill column className={"p-20px"} gap={"gap.medium"}>
                <Header className={"title"} as={"h2"} content={languageObject.translate("ChangeNotificationSettings")}/>
                <FormDropdown
                    inverted
                    items={this.state.availableLanguages && this.state.availableLanguages.map(l => ({
                        key: "language_" + l.id,
                        header: languageObject.translate(l.languageName)
                    }))}
                    value={languageObject.translate(this.state.selectedLanguage ? this.state.selectedLanguage.languageName : "English")}
                    label={languageObject.translate("SelectFavoriteLanguage")}
                    onChange={(e, data) => this.setState({selectedLanguage: this.state.availableLanguages[(data as any).highlightedIndex]})}
                />
                <Flex>
                    <Alert content={languageObject.translate("UserSettingsFavoriteLanguageMessage")} warning fitted/>
                </Flex>
                {
                    this.context.topics.list.length ?
                        <Flex column gap={"gap.medium"}>
                            <Text className={"subtitle"} content={languageObject.translate("SelectInterestingTopic")}/>
                            {this.context.topics.list.map((t, index) => {
                                let topicTitle = t.title;
                                let topicDesc = t.description;
                                const translation = t.translations && t.translations.filter(trans => {
                                    const language = this.state.availableLanguages.find(l => l.id === trans.languageId);
                                    return language?.code === this.state.locale;
                                })[0];
                                if (translation) {
                                    topicTitle = translation.title;
                                    topicDesc = translation.description;
                                }
                                return (
                                    <Flex key={t.id} vAlign={"center"} styles={{marginBottom: "15px"}}>
                                        <Checkbox
                                            toggle
                                            checked={this.state.selectedTopics[index]}
                                            disabled={t.cannotDeactivate}
                                            onClick={() => !this.context.topics.list[index].cannotDeactivate && this.setState({
                                                selectedTopics: update(this.state.selectedTopics, {
                                                    [index]: {$set: !this.state.selectedTopics[index]}
                                                })
                                            })}
                                        />
                                        <Flex column vAlign={"center"}>
                                            <Text weight={"semibold"} content={topicTitle}/>
                                            <Text size={"small"} content={topicDesc}/>
                                        </Flex>
                                    </Flex>
                                );
                            })}
                        </Flex>
                        :
                        <></>
                }
            </Flex>
        );
    }

}

export default UserSettings